/**
 * Surface-decoration registry for terrain tiles.
 *
 * A "decoration" is a small voxel sprig painted on top of a tile to
 * differentiate terrain at a glance (forest sprigs on green, rubble
 * piles on rubble, water caustic ripples). Each decoration is a pure
 * function `(rng) → Voxel[]` so per-hex output is deterministic when
 * the caller seeds the rng from `(map_seed, q, r, terrainId)`.
 *
 * Extending: add a new entry to `DECORATIONS`. Then reference its key
 * from `TerrainSpec.decoration` in `./catalog.ts`. No HexFloor code
 * changes.
 *
 * Voxel coords are top-face-local: x/z span the top of a 4×-resolution
 * hex tile (~24 voxels wide), y=0 is the tile's top surface.
 */

import { SeededRng } from "../../determinism/cosmeticSeed";
import type { Voxel } from "../types";

export type DecorationKind =
  | "forest_sprig"
  | "rubble_pile"
  | "rocky_outcrop"
  | "water_caustic";

export type DecorationFn = (rng: SeededRng) => Voxel[];

// ─── Forest sprig — cluster of small voxel trees ─────────────────────────

function forestSprig(rng: SeededRng): Voxel[] {
  const out: Voxel[] = [];
  const treeCount = 2 + rng.nextInt(3); // 2..4 trees per tile
  for (let i = 0; i < treeCount; i++) {
    const cx = rng.nextInt(11) - 5; // -5..5
    const cz = rng.nextInt(11) - 5;
    const h = 4 + rng.nextInt(4); // 4..7 voxels tall
    // Trunk
    out.push({ x: cx, y: 0, z: cz, slot: "secondary" });
    out.push({ x: cx, y: 1, z: cz, slot: "secondary" });
    // Crown — a 3-tall, 3-wide blob
    for (let y = 2; y < h; y++) {
      const r = y === h - 1 ? 0 : 1; // taper at top
      for (let dx = -r; dx <= r; dx++) {
        for (let dz = -r; dz <= r; dz++) {
          out.push({ x: cx + dx, y, z: cz + dz, slot: "accent" });
        }
      }
    }
  }
  return out;
}

// ─── Rubble pile — scattered small rocks ─────────────────────────────────

function rubblePile(rng: SeededRng): Voxel[] {
  const out: Voxel[] = [];
  const count = 4 + rng.nextInt(5); // 4..8 chunks
  for (let i = 0; i < count; i++) {
    const cx = rng.nextInt(13) - 6;
    const cz = rng.nextInt(13) - 6;
    const w = 1 + rng.nextInt(2);
    const h = 1 + rng.nextInt(2);
    const d = 1 + rng.nextInt(2);
    const slot = rng.next() > 0.7 ? "primary" : "secondary";
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) {
        for (let z = 0; z < d; z++) {
          out.push({ x: cx + x, y, z: cz + z, slot });
        }
      }
    }
  }
  return out;
}

// ─── Rocky outcrop — taller boulders, ridge accent ───────────────────────

function rockyOutcrop(rng: SeededRng): Voxel[] {
  const out: Voxel[] = [];
  const boulderCount = 1 + rng.nextInt(2); // 1..2 boulders
  for (let i = 0; i < boulderCount; i++) {
    const cx = rng.nextInt(9) - 4;
    const cz = rng.nextInt(9) - 4;
    const w = 3 + rng.nextInt(2);
    const h = 4 + rng.nextInt(3);
    const d = 3 + rng.nextInt(2);
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) {
        for (let z = 0; z < d; z++) {
          // Slight irregularity — drop 10% of top voxels
          if (y === h - 1 && rng.next() < 0.2) continue;
          out.push({
            x: cx + x,
            y,
            z: cz + z,
            slot: y === h - 1 ? "accent" : "secondary",
          });
        }
      }
    }
  }
  return out;
}

// ─── Water caustic — flat emissive ripple pattern ────────────────────────

function waterCaustic(rng: SeededRng): Voxel[] {
  const out: Voxel[] = [];
  const ripples = 3 + rng.nextInt(3);
  for (let i = 0; i < ripples; i++) {
    const cx = rng.nextInt(13) - 6;
    const cz = rng.nextInt(13) - 6;
    const r = 1 + rng.nextInt(2);
    for (let a = 0; a < 8; a++) {
      const angle = (a * Math.PI) / 4;
      out.push({
        x: cx + Math.round(Math.cos(angle) * r),
        y: 0,
        z: cz + Math.round(Math.sin(angle) * r),
        slot: "emissive",
      });
    }
  }
  return out;
}

export const DECORATIONS: Record<DecorationKind, DecorationFn> = {
  forest_sprig: forestSprig,
  rubble_pile: rubblePile,
  rocky_outcrop: rockyOutcrop,
  water_caustic: waterCaustic,
};
